import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { ModalController } from '@ionic/angular';
import { SubscriberService } from 'src/app/services/manager/subscriber.service';
import { OrderService } from 'src/app/services/manager/order.service';
import { AlertService } from 'src/app/services/base/alert.service';
import { StorageService } from 'src/app/services/data/storage.service';
import { LoginService } from 'src/app/services/auth/login.service';
import { BarcodeScannerService } from 'src/app/services/base/barcode-scanner.service';
import { OfflineService } from 'src/app/services/manager/offline.service';
import { CurrentOrderService, ORDER_CLOSED_MESSAGE } from 'src/app/services/manager/current-order.service';
import { ImageCacheService } from 'src/app/services/data/image-cache.service';
import { BarcodeLookupService } from 'src/app/services/data/barcode-lookup.service';
import { ImageLightboxComponent } from '../image-lightbox/image-lightbox.component';
import { ProductFilterSheetComponent } from '../product-filter-sheet/product-filter-sheet.component';

interface Product {
  id?: string;
  name: string;
  nameDe?: string | null;
  model: string;
  season?: string;
  color: string;
  colorname?: string;
  colorDe?: string | null;
  colorHex?: string | null;
  colorCode?: string | null;
  colorcard?: string | null;
  price?: number;
  cost?: number;
  active: boolean | string;
  sizes?: Array<{ size: string }>;
  material?: Array<{ key: string; name?: string; value: number }> | null;
  materialDe?: Array<{ key: string; name: string; value: number }> | null;
  compositionDe?: string | null;
  company?: string | null;
  karmaLastSeen?: string | null;
  karmaUnavailable?: boolean;
  karmaUnavailableSince?: string | null;
  description?: string | null;
  categories?: string[] | null;
  images?: string[] | null;
  barcodes?: { barcode: string }[] | null;
  [key: string]: any;
}

export interface ProductRow {
  model: Product[];
  samePrice: boolean;
  priceDisplay: string;
  // Bild-Cache
  thumbUrl: string | null;   // null = noch nicht geladen / deaktiviert
  thumbLoading: boolean;
  // UI-State
  expanded: boolean;
  // Schnellmengen: null wenn kein Auftrag offen / mehr als eine Größe
  quickQty: number | null;
  // Zeigt ob mind. eine Farbe dieses Modells bereits in der aktuellen Order ist
  inCurrentOrder: boolean;
}

const RECENTLY_USED_KEY = 'recentlyUsedProducts';
const MAX_RECENT = 10;
const SCAN_HISTORY_KEY = 'scanHistory';
const MAX_SCAN_HISTORY = 20;

@Component({
  selector: 'app-product-selector',
  templateUrl: './product-selector.component.html',
  styleUrls: ['./product-selector.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductSelectorComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @ViewChild(CdkVirtualScrollViewport) private viewport?: CdkVirtualScrollViewport;
  @Input() search = '';
  @Input() showSearch = true;

  destroy = new Subject<boolean>();
  searchSubject = new Subject<string>();

  adminMode = false;
  costDiscount = 0;
  showTranslations = false;

  productList: Product[] = [];
  productRows: ProductRow[] = [];

  searchTerm = '';

  selectedSeasons = new Set<string>();
  availableSeasons: string[] = [];

  selectedMaterials = new Set<string>();
  availableMaterials: Array<{ key: string; name: string; nameEn?: string }> = [];

  selectedColors = new Set<string>();
  availableColors: Array<{ colorname: string; colorDe?: string | null }> = [];

  selectedSizes = new Set<string>();
  availableSizes: string[] = [];

  private searchCache = new Map<string, Product[]>();

  showDescription = false;
  showComposition = false;
  showNameDe = true;
  showKarmaUnavailableBadgeInList = false;
  textSize: 'small' | 'medium' | 'large' = 'medium';

  // Admin-Filter: nur Farben mit karmaUnavailable=true anzeigen
  isAdmin = false;
  showKarmaUnavailableOnly = false;

  canAddProducts = false;
  currentOrderExists = false;

  private favoriteModels = new Set<string>();
  showFavoritesOnly = false;

  // Zuletzt verwendet (Model-Keys)
  private recentlyUsed: string[] = [];
  showRecentOnly = false;

  // Warenkorb-Filter
  showInOrderOnly = false;

  // Scan-History (nur echte Barcode-Scans)
  scanHistory: string[] = [];
  showScanHistoryOnly = false;

  scannerSupported = false;

  constructor(
    private sub: SubscriberService,
    private order: OrderService,
    private alert: AlertService,
    private storage: StorageService,
    private login: LoginService,
    private barcodeScanner: BarcodeScannerService,
    private offline: OfflineService,
    private currentOrder: CurrentOrderService,
    private imageCache: ImageCacheService,
    private modalCtrl: ModalController,
    private cdr: ChangeDetectorRef,
    private barcodeLookup: BarcodeLookupService,
  ) {}

  ngOnInit() {
    this.isAdmin = this.login.decodedToken?.role === 'admin';

    this.barcodeScanner.isSupported().then(s => {
      this.scannerSupported = s;
      this.cdr.markForCheck();
    });

    this.storage.getData<boolean>('showTranslations').then(v => {
      this.showTranslations = v === true;
      this.cdr.markForCheck();
    });

    this.subscribe();

    this.searchSubject.pipe(
      takeUntil(this.destroy),
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.searchTerm = term;
      this.filterProducts();
      this.cdr.markForCheck();
    });

    Promise.all([
      this.storage.getData<string[]>('selectedSeasons'),
      this.storage.getData<string[]>('selectedSizes'),
      this.storage.getData<string[]>('selectedMaterials'),
      this.storage.getData<string[]>('selectedColors'),
      this.storage.getData<string[]>('favoriteProducts'),
      this.storage.getData<string[]>(RECENTLY_USED_KEY),
      this.storage.getData<boolean>('showDescription'),
      this.storage.getData<boolean>('showComposition'),
      this.storage.getData<boolean>('showNameDe'),
      this.storage.getData<string[]>(SCAN_HISTORY_KEY),
      this.storage.getData<boolean>('showKarmaUnavailableBadgeInList'),
    ]).then(([seasons, sizes, materials, colors, favs, recent, showDesc, showComp, showNameDe, scanHist, showUnavailBadge]) => {
      this.selectedSeasons = Array.isArray(seasons) ? new Set(seasons) : new Set(['PE27']);
      if (Array.isArray(sizes)) this.selectedSizes = new Set(sizes);
      if (Array.isArray(materials)) this.selectedMaterials = new Set(materials);
      if (Array.isArray(colors)) this.selectedColors = new Set(colors);
      if (Array.isArray(favs)) this.favoriteModels = new Set(favs);
      if (Array.isArray(recent)) this.recentlyUsed = recent;
      if (Array.isArray(scanHist)) this.scanHistory = scanHist;
      if (showDesc != null) this.showDescription = showDesc;
      if (showComp != null) this.showComposition = showComp;
      if (showNameDe != null) this.showNameDe = showNameDe;
      this.showKarmaUnavailableBadgeInList = showUnavailBadge === true;
      this.searchCache.clear();
      this.filterProducts();
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['search']) {
      this.searchSubject.next(changes['search'].currentValue);
    }
  }

  ngAfterViewInit() {
    // Erster Render: CDK hat die Rows jetzt im DOM — Thumbs nachladen
    setTimeout(() => this.prefetchVisibleThumbs(), 0);
  }

  ngOnDestroy() {
    this.destroy.next(true);
    this.destroy.complete();
  }

  subscribe() {
    this.sub.adminMode.pipe(takeUntil(this.destroy)).subscribe((data) => {
      this.adminMode = data;
      this.rebuildPriceDisplays();
      this.cdr.markForCheck();
    });

    this.sub.productList.pipe(takeUntil(this.destroy)).subscribe((data) => {
      if (data === this.productList) return;
      this.productList = data || [];
      this.searchCache.clear();
      this.extractAvailableSeasons();
      this.extractAvailableMaterials();
      this.extractAvailableSizes();
      this.extractAvailableColors();
      this.filterProducts();
      this.cdr.markForCheck();
    });

    this.sub.currentOrder.pipe(takeUntil(this.destroy)).subscribe(() => {
      this.canAddProducts = this.computeCanAddProducts();
      // Ohne offenen Auftrag führt der Button zum ActionSheet — dann ist er
      // regulär nutzbar und darf nicht ausgegraut wirken.
      this.currentOrderExists = !!this.sub.currentOrder.getValue();
      this.productRows = this.productRows.map(r => ({
        ...r,
        quickQty: this.computeQuickQty(r.model),
        inCurrentOrder: this.computeInCurrentOrder(r.model),
      }));
      this.cdr.markForCheck();
    });
  }

  private computeCanAddProducts(): boolean {
    // Regel liegt zentral im CurrentOrderService, damit UI und Schreibpfad
    // nicht auseinanderlaufen können.
    return this.currentOrder.canModifyProducts();
  }

  private computeInCurrentOrder(model: Product[]): boolean {
    const order = this.sub.currentOrder.getValue();
    if (!order?.products) return false;
    const modelKey = model[0]?.model;
    return (order.products as any[]).some(p => p.model === modelKey);
  }

  /** Schnellmenge ist nur sinnvoll wenn genau eine Größe existiert */
  private computeQuickQty(model: Product[]): number | null {
    if (!this.canAddProducts) return null;
    const sizes = model[0]?.sizes;
    if (!sizes || sizes.length !== 1) return null;
    // Aktuelle Menge aus currentOrder lesen
    const order = this.sub.currentOrder.getValue();
    if (!order?.products) return 0;
    const existing = (order.products as any[]).find(
      p => p.model === model[0].model && p.color === model[0].color
    );
    return existing?.sizes?.[0]?.quantity ?? 0;
  }

  onSearchChange(event: any) {
    this.searchSubject.next(event.target.value);
  }

  extractAvailableSeasons() {
    if (!this.productList.length) return;
    this.availableSeasons = [...new Set(this.productList.map(p => p.season))]
      .filter((s): s is string => !!s)
      .sort();
  }

  private static readonly MATERIAL_NAMES: Record<string, { de: string; en: string }> = {
    'CO': { de: 'Baumwolle', en: 'Cotton' },
    'WO': { de: 'Wolle', en: 'Wool' },
    'PL': { de: 'Polyester', en: 'Polyester' },
    'PA': { de: 'Polyamid', en: 'Polyamide' },
    'EL': { de: 'Elasthan', en: 'Elastane' },
    'EA': { de: 'Elasthan', en: 'Elastane' },
    'SE': { de: 'Seide', en: 'Silk' },
    'LI': { de: 'Leinen', en: 'Linen' },
    'VI': { de: 'Viskose', en: 'Viscose' },
    'CV': { de: 'Viskose', en: 'Viscose' },
    'MA': { de: 'Modal', en: 'Modal' },
    'AC': { de: 'Acryl', en: 'Acrylic' },
    'WS': { de: 'Kaschmir', en: 'Cashmere' },
    'WK': { de: 'Kaschmir', en: 'Cashmere' },
    'TA': { de: 'Triacetat', en: 'Triacetate' },
    'CU': { de: 'Cupro', en: 'Cupro' },
    'HL': { de: 'Hanf', en: 'Hemp' },
  };

  extractAvailableMaterials() {
    if (!this.productList.length) return;
    const mapDe = new Map<string, string>();
    const mapEn = new Map<string, string>();
    for (const p of this.productList) {
      const de = Array.isArray(p.materialDe) && p.materialDe.length ? p.materialDe : null;
      const en = Array.isArray(p['material']) && p['material'].length ? p['material'] : null;
      for (const m of de ?? []) {
        if (m.key && !mapDe.has(m.key)) mapDe.set(m.key, m.name || m.key);
      }
      for (const m of en ?? []) {
        if (m.key && !mapEn.has(m.key)) mapEn.set(m.key, m.name || m.key);
      }
    }
    const keys = new Set([...mapDe.keys(), ...mapEn.keys()]);
    this.availableMaterials = [...keys]
      .map(key => {
        const fallback = ProductSelectorComponent.MATERIAL_NAMES[key.toUpperCase()];
        // mapDe = aus materialDe (immer Deutsch)
        // mapEn = aus material (bei Karma-Produkten auch Deutsch, bei anderen ggf. Englisch)
        // Fallback-Map hat zuverlässige DE/EN Werte
        const deName = mapDe.get(key) || fallback?.de || key;
        const enName = fallback?.en || mapEn.get(key) || key;
        return { key, name: deName, nameEn: enName };
      })
      .sort((a, b) => a.key.localeCompare(b.key));
  }

  extractAvailableColors() {
    if (!this.productList.length) return;
    const seen = new Map<string, string | null | undefined>();
    for (const p of this.productList) {
      const key = (p.colorname || p.color || '').toUpperCase();
      if (key && !seen.has(key)) seen.set(key, p.colorDe);
    }
    this.availableColors = [...seen.entries()]
      .map(([colorname, colorDe]) => ({ colorname, colorDe }))
      .sort((a, b) => a.colorname.localeCompare(b.colorname));
  }

  extractAvailableSizes() {
    if (!this.productList.length) return;
    const sizes = new Set<string>();
    for (const p of this.productList) {
      if (Array.isArray(p.sizes)) {
        for (const s of p.sizes) if (s.size) sizes.add(s.size);
      }
    }
    const sizeOrder = ['XXS','XS','S','M','L','XL','XXL','XXXL','3XL','4XL'];
    this.availableSizes = [...sizes].sort((a, b) => {
      const ai = sizeOrder.indexOf(a);
      const bi = sizeOrder.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });
  }

  toggleSeason(season: string) {
    if (this.selectedSeasons.has(season)) {
      this.selectedSeasons.delete(season);
    } else {
      this.selectedSeasons.add(season);
    }
    this.searchCache.clear();
    this.filterProducts();
    this.storage.setData('selectedSeasons', [...this.selectedSeasons]);
    this.cdr.markForCheck();
  }

  toggleMaterial(key: string) {
    if (this.selectedMaterials.has(key)) {
      this.selectedMaterials.delete(key);
    } else {
      this.selectedMaterials.add(key);
    }
    this.searchCache.clear();
    this.filterProducts();
    this.storage.setData('selectedMaterials', [...this.selectedMaterials]);
    this.cdr.markForCheck();
  }

  toggleColor(colorname: string) {
    if (this.selectedColors.has(colorname)) {
      this.selectedColors.delete(colorname);
    } else {
      this.selectedColors.add(colorname);
    }
    this.searchCache.clear();
    this.filterProducts();
    this.storage.setData('selectedColors', [...this.selectedColors]);
    this.cdr.markForCheck();
  }

  toggleSize(size: string) {
    if (this.selectedSizes.has(size)) {
      this.selectedSizes.delete(size);
    } else {
      this.selectedSizes.add(size);
    }
    this.searchCache.clear();
    this.filterProducts();
    this.storage.setData('selectedSizes', [...this.selectedSizes]);
    this.cdr.markForCheck();
  }

  // ── Filter ────────────────────────────────────────────────────────────────

  filterProducts() {
    let filtered = this.productList.filter(p => p.active !== false && p.active !== 'false');

    if (this.showRecentOnly) {
      filtered = filtered.filter(p => this.recentlyUsed.includes(p.model));
    }

    if (this.showFavoritesOnly) {
      filtered = filtered.filter(p => this.favoriteModels.has(p.model));
    }

    if (this.showInOrderOnly) {
      const inOrder = this.currentOrderProductModels;
      filtered = filtered.filter(p => inOrder.has(p.model));
    }

    if (this.showScanHistoryOnly) {
      const scanModels = this.scanHistory.filter(e => !e.startsWith('[Code:'));
      filtered = filtered.filter(p => scanModels.includes(p.model));
    }

    if (this.isAdmin && this.showKarmaUnavailableOnly) {
      // Alle Farben des betroffenen Modells zeigen (nicht nur die unavailable Variante
      // selbst) — so ist auf einen Blick sichtbar, welche Alternativfarbe noch da ist.
      const affectedModels = new Set(
        filtered.filter(p => (p as any).karmaUnavailable === true).map(p => p.model)
      );
      filtered = filtered.filter(p => affectedModels.has(p.model));
    }

    if (this.selectedSeasons.size > 0) {
      filtered = filtered.filter(p => p.season != null && this.selectedSeasons.has(p.season));
    }

    if (this.selectedMaterials.size > 0) {
      filtered = filtered.filter(p =>
        Array.isArray(p['material']) &&
        p['material'].some((m: any) => this.selectedMaterials.has(m.key))
      );
    }

    if (this.selectedSizes.size > 0) {
      filtered = filtered.filter(p =>
        Array.isArray(p.sizes) &&
        p.sizes.some((s: any) => this.selectedSizes.has(s.size))
      );
    }

    if (this.selectedColors.size > 0) {
      filtered = filtered.filter(p =>
        this.selectedColors.has((p.colorname || p.color || '').toUpperCase())
      );
    }

    if (this.searchTerm) {
      const seasonKey = [...this.selectedSeasons].join(',');
      const matKey = [...this.selectedMaterials].join(',');
      const sizeKey = [...this.selectedSizes].join(',');
      const colorKey = [...this.selectedColors].join(',');
      const cacheKey = `${this.showRecentOnly}::${this.showFavoritesOnly}::${this.showKarmaUnavailableOnly}::${seasonKey}::${matKey}::${sizeKey}::${colorKey}::${this.searchTerm}`;
      const cached = this.searchCache.get(cacheKey);
      if (cached) {
        this.productRows = this.buildRows(this.groupByModel(cached));
        setTimeout(() => this.prefetchVisibleThumbs(), 0);
        return;
      }
      const lower = this.searchTerm.toLowerCase();
      const fields = ['name', 'nameDe', 'model', 'season', 'color', 'colorname', 'colorDe'];
      filtered = filtered.filter(item =>
        fields.some(f => {
          const v = item[f];
          return v != null && v.toString().toLowerCase().includes(lower);
        }) ||
        item.barcodes?.some((b: any) => b.barcode === this.searchTerm)
      );
      this.searchCache.set(cacheKey, filtered);
    }

    this.productRows = this.buildRows(this.groupByModel(filtered));
    setTimeout(() => this.prefetchVisibleThumbs(), 0);
  }

  private groupByModel(list: Product[]): Product[][] {
    if (!list.length) return [];
    const map = new Map<string, Product[]>();
    for (const item of list) {
      const key = item.model || '';
      const existing = map.get(key);
      if (existing) existing.push(item);
      else map.set(key, [item]);
    }
    // Zuletzt-verwendet an den Anfang sortieren
    if (this.showRecentOnly || this.recentlyUsed.length) {
      return Array.from(map.values()).sort((a, b) => {
        const ai = this.recentlyUsed.indexOf(a[0].model);
        const bi = this.recentlyUsed.indexOf(b[0].model);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
    }
    return Array.from(map.values());
  }

  private buildRows(groups: Product[][]): ProductRow[] {
    return groups.map(model => ({
      model,
      samePrice: model.length <= 1 || model.every(p => p.price === model[0].price),
      priceDisplay: this.formatPrice(model[0].price),
      thumbUrl: null,
      thumbLoading: false,
      expanded: false,
      quickQty: this.computeQuickQty(model),
      inCurrentOrder: this.computeInCurrentOrder(model),
    }));
  }

  /** Lädt Thumbnails für alle aktuell im Viewport gerenderten Rows */
  prefetchVisibleThumbs() {
    if (this.viewport) {
      const { start, end } = this.viewport.getRenderedRange();
      for (let i = start; i < end && i < this.productRows.length; i++) {
        this.loadThumb(this.productRows[i]);
      }
    } else {
      // Fallback vor ViewInit: erste 15 laden
      const limit = Math.min(this.productRows.length, 15);
      for (let i = 0; i < limit; i++) {
        this.loadThumb(this.productRows[i]);
      }
    }
  }

  loadThumb(row: ProductRow) {
    if (row.thumbUrl !== null || row.thumbLoading) return;
    if (!this.imageCache.enabled) {
      row.thumbUrl = null;
      return;
    }
    const url = row.model[0].images?.[0];
    if (!url) return;
    row.thumbLoading = true;
    this.imageCache.resolve(url).then(dataUrl => {
      row.thumbUrl = dataUrl;
      row.thumbLoading = false;
      this.cdr.markForCheck();
    });
  }

  private rebuildPriceDisplays() {
    if (!this.productRows.length) return;
    this.productRows = this.productRows.map(r => ({
      ...r,
      priceDisplay: this.formatPrice(r.model[0].price),
    }));
  }

  private formatPrice(price: number | undefined): string {
    if (!price) return '';
    return price.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  }

  // ── Favoriten ────────────────────────────────────────────────────────────

  isFavorite(modelKey: string): boolean {
    return this.favoriteModels.has(modelKey);
  }

  toggleFavorite(modelKey: string, event: Event) {
    event.stopPropagation();
    if (this.favoriteModels.has(modelKey)) {
      this.favoriteModels.delete(modelKey);
    } else {
      this.favoriteModels.add(modelKey);
    }
    this.storage.setData('favoriteProducts', [...this.favoriteModels]);
    if (this.showFavoritesOnly) this.filterProducts();
    this.cdr.markForCheck();
  }

  toggleFavoritesFilter() {
    this.showFavoritesOnly = !this.showFavoritesOnly;
    if (this.showFavoritesOnly) {
      this.showRecentOnly = false;
      this.showInOrderOnly = false;
      this.showScanHistoryOnly = false;
      this.showKarmaUnavailableOnly = false;
    }
    this.filterProducts();
    this.cdr.markForCheck();
  }

  toggleInOrderFilter() {
    this.showInOrderOnly = !this.showInOrderOnly;
    if (this.showInOrderOnly) {
      this.showRecentOnly = false;
      this.showFavoritesOnly = false;
      this.showScanHistoryOnly = false;
      this.showKarmaUnavailableOnly = false;
    }
    this.filterProducts();
    this.cdr.markForCheck();
  }

  toggleScanHistoryFilter() {
    this.showScanHistoryOnly = !this.showScanHistoryOnly;
    if (this.showScanHistoryOnly) {
      this.showRecentOnly = false;
      this.showFavoritesOnly = false;
      this.showInOrderOnly = false;
      this.showKarmaUnavailableOnly = false;
    }
    this.filterProducts();
    this.cdr.markForCheck();
  }

  toggleKarmaUnavailableFilter() {
    this.showKarmaUnavailableOnly = !this.showKarmaUnavailableOnly;
    if (this.showKarmaUnavailableOnly) {
      this.showRecentOnly = false;
      this.showFavoritesOnly = false;
      this.showInOrderOnly = false;
      this.showScanHistoryOnly = false;
    }
    this.filterProducts();
    this.cdr.markForCheck();
  }

  get currentOrderProductModels(): Set<string> {
    const order = this.sub.currentOrder.getValue();
    if (!order?.products) return new Set();
    return new Set((order.products as any[]).map((p: any) => p.model));
  }

  // ── Zuletzt verwendet ────────────────────────────────────────────────────

  isRecentlyUsed(modelKey: string): boolean {
    return this.recentlyUsed.includes(modelKey);
  }

  private trackRecentlyUsed(modelKey: string) {
    this.recentlyUsed = [modelKey, ...this.recentlyUsed.filter(k => k !== modelKey)].slice(0, MAX_RECENT);
    this.storage.setData(RECENTLY_USED_KEY, this.recentlyUsed);
  }

  get activeFilterCount(): number {
    return this.selectedSeasons.size + this.selectedSizes.size + this.selectedMaterials.size + this.selectedColors.size;
  }

  get selectedSeasonsArray(): string[] { return [...this.selectedSeasons]; }
  get selectedSizesArray(): string[] { return [...this.selectedSizes]; }
  get selectedMaterialsArray(): string[] { return [...this.selectedMaterials]; }
  get selectedColorsArray(): string[] { return [...this.selectedColors]; }

  async openFilterSheet() {
    const modal = await this.modalCtrl.create({
      component: ProductFilterSheetComponent,
      cssClass: 'filter-sheet-modal',
      backdropDismiss: false,
      componentProps: {
        availableSeasons: this.availableSeasons,
        availableSizes: this.availableSizes,
        availableMaterials: this.availableMaterials,
        availableColors: this.availableColors,
        selectedSeasons: new Set(this.selectedSeasons),
        selectedSizes: new Set(this.selectedSizes),
        selectedMaterials: new Set(this.selectedMaterials),
        selectedColors: new Set(this.selectedColors),
        showTranslations: this.showTranslations,
        showDescription: this.showDescription,
        showComposition: this.showComposition,
      },
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data) {
      this.selectedSeasons = data.selectedSeasons;
      this.selectedSizes = data.selectedSizes;
      this.selectedMaterials = data.selectedMaterials;
      this.selectedColors = data.selectedColors;
      this.showDescription = data.showDescription;
      this.showComposition = data.showComposition;
      this.searchCache.clear();
      this.filterProducts();
      this.storage.setData('selectedSeasons', [...this.selectedSeasons]);
      this.storage.setData('selectedSizes', [...this.selectedSizes]);
      this.storage.setData('selectedMaterials', [...this.selectedMaterials]);
      this.storage.setData('selectedColors', [...this.selectedColors]);
      this.storage.setData('showDescription', this.showDescription);
      this.storage.setData('showComposition', this.showComposition);
      this.cdr.markForCheck();
    }
  }

  resetFilters() {
    this.selectedSeasons.clear();
    this.selectedSizes.clear();
    this.selectedMaterials.clear();
    this.selectedColors.clear();
    this.searchCache.clear();
    this.filterProducts();
    this.storage.setData('selectedSeasons', []);
    this.storage.setData('selectedSizes', []);
    this.storage.setData('selectedMaterials', []);
    this.storage.setData('selectedColors', []);
    this.cdr.markForCheck();
  }

  toggleRecentFilter() {
    this.showRecentOnly = !this.showRecentOnly;
    if (this.showRecentOnly) {
      this.showFavoritesOnly = false;
      this.showInOrderOnly = false;
      this.showScanHistoryOnly = false;
    }
    this.filterProducts();
    this.cdr.markForCheck();
  }

  // ── Expand / Collapse ────────────────────────────────────────────────────

  toggleExpand(row: ProductRow, event: Event) {
    event.stopPropagation();
    row.expanded = !row.expanded;
    this.cdr.markForCheck();
  }

  // ── Schnellmengen ────────────────────────────────────────────────────────

  incrementQuickQty(row: ProductRow, event: Event) {
    event.stopPropagation();
    if (row.quickQty === null) return;
    if (!row.model[0].price || Number(row.model[0].price) === 0) {
      this.alert.presentToast('Produkt hat keinen gültigen Preis (0,00 €)');
      return;
    }
    row.quickQty++;
    this.saveQuickQty(row);
    this.cdr.markForCheck();
  }

  decrementQuickQty(row: ProductRow, event: Event) {
    event.stopPropagation();
    if (row.quickQty === null || row.quickQty <= 0) return;
    row.quickQty--;
    this.saveQuickQty(row);
    this.cdr.markForCheck();
  }

  private saveQuickQty(row: ProductRow) {
    if (row.quickQty === null) return;
    const product = structuredClone(row.model[0]) as any;
    if (!product.sizes?.length) return;
    product.sizes = [{ ...product.sizes[0], quantity: row.quickQty }];
    this.currentOrder.addOrUpdateProducts([product]);
  }

  // ── Produkt hinzufügen ────────────────────────────────────────────────────

  async addProduct(model: Product[]) {
    if (!model?.length) return;

    if (model[0].active === false || model[0].active === 'false') {
      this.alert.presentToast('Produkt ist nicht aktiv');
      return;
    }

    if (!model[0].price || Number(model[0].price) === 0) {
      this.alert.presentToast('Produkt hat keinen gültigen Preis (0,00 €)');
      return;
    }

    const curr = this.sub.currentOrder.getValue();
    if (curr) {
      // Abgeschlossener Auftrag: Button ist ausgegraut, der Klick erklärt warum.
      if (!this.canAddProducts) {
        this.alert.presentToast(ORDER_CLOSED_MESSAGE);
        return;
      }
      this.trackRecentlyUsed(model[0].model);
      this.order.openProductModal(model);
      return;
    }

    this.alert.presentActionSheet('Kein Auftrag offen', [
      {
        text: 'Neuen Auftrag erstellen',
        icon: 'add-circle-outline',
        handler: async () => {
          await this.offline.createOfflineOrder(true);
          setTimeout(() => {
            if (this.sub.currentOrder.getValue()) {
              this.trackRecentlyUsed(model[0].model);
              this.order.openProductModal(model);
            }
          }, 100);
        },
      },
      {
        text: 'Zu bestehender Order hinzufügen',
        icon: 'list-outline',
        handler: () => this.pickExistingOrder(model),
      },
      { text: 'Abbrechen', role: 'cancel', icon: 'close-outline' },
    ]);
  }

  private async pickExistingOrder(productData: Product[]) {
    const orders = this.sub.orderList.getValue().filter((o: any) =>
      (o.state === 0 || o.state == null) && o.status === 0
    ).reverse();
    if (!orders.length) {
      this.alert.presentToast('Keine offenen Aufträge vorhanden');
      return;
    }

    const buttons = orders.map((o: any) => {
      const customer = o.customerBase?.name || o.customerBase?.lastName || '';
      const date = o.created ? new Date(o.created).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '';
      const label = [o.orderTitle || o.orderId, customer, date].filter(Boolean).join(' · ');
      return {
        text: label.slice(0, 80),
        handler: () => {
          this.offline.setCurrentOrder(o);
          setTimeout(() => {
            this.trackRecentlyUsed(productData[0].model);
            this.order.openProductModal(productData);
          }, 100);
        },
      };
    });
    buttons.push({ text: 'Abbrechen', role: 'cancel' } as any);
    this.alert.presentActionSheet('Auftrag wählen', buttons);
  }

  // ── Bild-Cache Getter ─────────────────────────────────────────────────────

  get imagesEnabled(): boolean {
    return this.imageCache.enabled;
  }

  get currentOrderTitle(): string | null {
    const o = this.sub.currentOrder.getValue();
    return o?.orderTitle ?? null;
  }

  proxyUrl(url: string | null | undefined): string {
    if (!url) return '';
    return this.imageCache.proxyUrl(url);
  }

  private static readonly COLOR_MAP: Record<string, string> = {
    // Weiß / Creme
    'bianco': '#f2f2f2', 'white': '#f2f2f2',
    'gesso': '#ede8de', 'margarina': '#f0ead2',
    'naturale': '#e8e0cc', 'panna': '#f0e6d0', 'avorio': '#e8ddb5',
    'burro': '#f5e6c0', 'perla': '#e8e2d8', 'pearl': '#e8e2d8',
    'nude': '#e0c8b0', 'ghiaccio': '#ddeef5',
    'latte': '#d8c4a0',
    // Grau
    'grigio': '#9e9e9e', 'grey': '#9e9e9e', 'gray': '#9e9e9e',
    'grigio chiaro': '#d0d0d0', 'grigiochiaro': '#d0d0d0',
    'grigio medio': '#8a8a8a', 'grigio medio melange': '#8a8a8a', 'grigiomediomelange': '#8a8a8a',
    'grigio scuro': '#555', 'grigio melange': '#a0a0a0',
    'antracite': '#455060', 'anthracite': '#455060', 'graphite': '#505a60',
    'piombo': '#6a7480', 'lavagna': '#5a6068', 'cemento': '#909090',
    'lavaggio scuro': '#606878',
    // Schwarz
    'nero': '#1a1a1a', 'black': '#1a1a1a', 'moro': '#141414',
    'nero/antracite': '#2a2a35',
    // Beige / Sand / Braun
    'beige': '#d8c8a8', 'sabbia': '#c8b48a', 'sand': '#c8b48a',
    'corda': '#b09060', 'corda melange': '#b89870',
    'tabacco': '#8b6340', 'caffe': '#6f4e37', 'cappuccino': '#9b7653',
    'nocciola': '#9c6b3c', 'cammello': '#c09850',
    'cioccolato': '#6b3a1f', 'cacao': '#5a2e10',
    'fango': '#8c7655', 'taupe': '#8b7d72', 'tortora': '#9e8c7e',
    'mastice': '#b0a890',
    // Rot / Bordeaux / Weinrot
    'rosso': '#c0392b', 'red': '#c0392b',
    'amarone': '#8b1a2a', 'amaranto': '#8b1a40',
    'bordeaux': '#6b1020', 'burgundy': '#6b1020',
    'vino': '#6b1428', 'mosto': '#5a1030',
    'prugna': '#7a1848', 'melanzana': '#6a1545',
    'ruggine': '#b04010',
    // Orange / Gelb
    'arancione': '#e07820', 'orange': '#e07820',
    'senape': '#b88c10', 'kaky': '#8b8640', 'khaki': '#8b8640',
    'giallo': '#f0c020', 'yellow': '#f0c020',
    'ocra': '#c88c20', 'oro': '#d4a010',
    // Grün
    'verde': '#3a8040',
    'verde militare': '#4a5230', 'militare': '#4a5230',
    'salvia': '#8a9a58', 'verde salvia': '#8a9a58',
    'bottiglia': '#1a4a30',
    'kaki': '#8b864e',
    'menta': '#80c8a8', 'acqua': '#00bcd4',
    // Blau
    'blu': '#1a3a7a', 'blue': '#1a3a7a',
    'navy': '#0d1b4b', 'blu navy': '#0d1b4b',
    'denim': '#2a5080', 'jeans': '#2a5080',
    'avio': '#4a6a90', 'blu avio': '#4a6a90', 'bluette': '#5a80b0',
    'azzurro': '#5090c0', 'celeste': '#80b8e0',
    'indaco': '#3040a0', 'cobalto': '#0040a0',
    'ottanio': '#1a7070', 'petrol': '#1a7070',
    // Lila / Violett
    'viola': '#6040a0',
    'lavanda': '#9090c0', 'lilla': '#b080c0', 'lilac': '#b080c0',
    'malva': '#b07090', 'mauve': '#b07090',
    'ametista': '#8040a0',
    // Rosa / Pink
    'rosa': '#e080a0', 'pink': '#e080a0',
    'cipria': '#e0a090', 'silver pink': '#c8a0a8',
    'fucsia': '#d01880', 'fuchsia': '#d01880',
    'salmone': '#e08070', 'corallo': '#e06858',
    // Gemischte Slugs aus der DB
    'verde-militare': '#4a5230',
    'latteblucoccio': '#8a7a60',
    'masticelatte': '#c0b090',
    'militare-latte': '#7a7a58',
    'lav-blu-medio': '#7080a8',
    'coccio': '#a05c48',
    'piombo/nero': '#404850',
  };

  colorHexFallback(item: Product): string {
    if (item.colorHex) return item.colorHex;

    const normalize = (s: string) => s.toLowerCase().trim().replace(/\s+/g, ' ');

    // 1. Slug direkt
    const slug = normalize(item.color || '');
    if (ProductSelectorComponent.COLOR_MAP[slug]) return ProductSelectorComponent.COLOR_MAP[slug];

    // 2. colorname direkt (z.B. "BIANCO", "BLU")
    const nameKey = normalize(item.colorname || '');
    if (ProductSelectorComponent.COLOR_MAP[nameKey]) return ProductSelectorComponent.COLOR_MAP[nameKey];

    // 3. Erstes Wort aus colorname (bei Kombinationen wie "BIANCO/NERO" → "bianco")
    const firstWord = nameKey.split(/[\s/]/)[0];
    if (firstWord && ProductSelectorComponent.COLOR_MAP[firstWord]) return ProductSelectorComponent.COLOR_MAP[firstWord];

    // 4. Einzelne Teile des Slugs
    for (const part of slug.split(/[-/]/)) {
      if (ProductSelectorComponent.COLOR_MAP[part]) return ProductSelectorComponent.COLOR_MAP[part];
    }

    return '#888';
  }

  // ── TrackBy ───────────────────────────────────────────────────────────────

  trackByRow(index: number, row: ProductRow): string {
    return row.model[0]?.model ?? index.toString();
  }

  trackByColor(index: number, item: Product): string {
    return item.id || item.color || index.toString();
  }

  trackBySize(index: number, size: any): string {
    return size.size || index.toString();
  }

  // ── Misc ──────────────────────────────────────────────────────────────────

  async scanBarcode() {
    const value = await this.barcodeScanner.scan();
    if (!value) return;
    const sku = this.barcodeLookup.lookup(value);
    const searchTerm = sku ?? value;
    this.searchSubject.next(searchTerm);
    const entry = sku ?? `[Code: ${value}]`;
    this.scanHistory = [entry, ...this.scanHistory.filter(e => e !== entry)].slice(0, MAX_SCAN_HISTORY);
    this.storage.setData(SCAN_HISTORY_KEY, this.scanHistory);
    this.cdr.markForCheck();
  }

  async openLightbox(images: string[], startIndex = 0) {
    const modal = await this.modalCtrl.create({
      component: ImageLightboxComponent,
      componentProps: { images, startIndex },
      cssClass: 'lightbox-modal',
    });
    await modal.present();
  }
}
